/**
 * Step 3 — Link creative authoring (A4.3.3).
 *
 * Renders `AdvantagePlusCreativePanel` at the top so the user makes the
 * Standard / Advantage+ decision before authoring the fields they're
 * about to write. (When Advantage+ is on, the warning Alert sets
 * expectations that the fields they're filling out may be auto-varied.)
 *
 * **Image selection.** Reuses Google's `TargetedImagePicker` — the same
 * platform-aware picker `CreateRDAWizard` / `CreateDemandGenAdWizard`
 * use. Three buttons let the user pick the aspect ratio (Feed 1:1, Feed
 * 1.91:1, Story 9:16); each opens the picker scoped to the matching
 * `meta_ads.*` placement key in the spec registry. The picker emits a
 * `PendingTargeted[]` (in-memory crop) which the wizard materializes at
 * submit time — same lazy-materialization contract as Google's wizards.
 *
 * Live char counts on the three text fields — the count goes red once
 * it crosses the soft cap so users know they're going to hit a
 * validation error on submit.
 *
 * Functional setter pattern on `onChange` — PR #181 round-1 + round-3
 * lesson. Any multi-field update path needs to read prev state.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { Input } from '@/components/ui/Input';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import {
  metaAdsApi,
  type MetaAdvantagePlusCreativeSpec,
  type MetaCallToActionType,
  type MetaLeadForm,
  type MetaLinkCreativeSpec,
} from '@/services/api/metaAds';
import type { PendingTargeted } from '@/types/media';

import {
  CALL_TO_ACTION_OPTIONS,
  LINK_CHAR_LIMITS,
  META_PLACEMENT_LABELS,
  META_PLACEMENT_OPTIONS,
} from '../../constants/metaAdMatrix';
import { AdvantagePlusCreativePanel } from './AdvantagePlusCreativePanel';

export interface LinkCreativeStepProps {
  value: MetaLinkCreativeSpec;
  onChange: React.Dispatch<React.SetStateAction<MetaLinkCreativeSpec>>;
  /** Pending image crop the user picked via TargetedImagePicker.
   *  Materialized to a GCS URL at wizard submit time and re-uploaded to
   *  Meta's /act_X/adimages endpoint there. `null` = user hasn't picked
   *  an image yet. */
  pendingImage: PendingTargeted | null;
  onChangePendingImage: (next: PendingTargeted | null) => void;
  advantagePlusCreative: MetaAdvantagePlusCreativeSpec;
  onChangeAdvantagePlusCreative: React.Dispatch<
    React.SetStateAction<MetaAdvantagePlusCreativeSpec>
  >;
  /** True when the parent campaign objective is OUTCOME_LEADS AND the
   *  parent ad set's destination_type is compatible with instant forms
   *  (ON_AD or unresolved — the two states where Meta accepts a form-
   *  attached creative). When true the step renders a lead-form picker;
   *  the picked form is stamped onto `value.lead_gen_form_id`. Renamed
   *  from `isLeadsObjective` in Copilot round 5 on PR #216 to reflect
   *  the composite gate: the parent objective alone doesn't determine
   *  whether the picker is applicable. See `parentDestinationType` for
   *  the raw destination value driving the fallback branch. */
  isLeadsFormPickerApplicable?: boolean;
  /** Parent ad set's `destination_type`. When the parent objective IS
   *  OUTCOME_LEADS but `isLeadsFormPickerApplicable` is false (non-
   *  ON_AD destination), this is used to render a small info line
   *  telling the user why the form picker is absent. `null` when
   *  unresolved. */
  parentDestinationType?: string | null;
  /** Facebook Page ID the ad set targets (inherited from parent for LEADS).
   *  Used to query `metaAdsApi.listLeadForms(pageId, adAccountId)`. */
  pageId?: string;
  /** Ad account ID — needed for the Meta token lookup on the backend
   *  when fetching lead forms. */
  adAccountId?: string;
  /** Callback with the picked form's `name`, so the wizard can pass it
   *  to `AdReviewStep` for display without a second query. */
  onLeadFormNameChange?: (name: string | null) => void;
  errors?: {
    image?: string;
    headline?: string;
    primary_text?: string;
    description?: string;
    link_url?: string;
    lead_gen_form_id?: string;
  };
  /** URL of the ad's current image, when reusing this step in an
   *  editor context (`MetaAdEditor`). When `pendingImage` is null and
   *  `existingImageUrl` is set, the step renders the current image
   *  with a "Replace image" button instead of the aspect-ratio picker
   *  grid (which is the wizard's "no image yet" state). Unset in the
   *  wizard — Meta's Advantage+ Placements picks the ratio then, so
   *  the initial state is the picker grid. */
  existingImageUrl?: string;
}

function CharCount({
  used,
  cap,
}: {
  used: number;
  cap: number;
}): React.ReactElement {
  const over = used > cap;
  return (
    <span
      className={
        over
          ? 'text-xs font-medium text-red-600'
          : 'text-xs text-gray-500'
      }
    >
      {used}/{cap}
    </span>
  );
}

export const LinkCreativeStep: React.FC<LinkCreativeStepProps> = ({
  value,
  onChange,
  pendingImage,
  onChangePendingImage,
  advantagePlusCreative,
  onChangeAdvantagePlusCreative,
  isLeadsFormPickerApplicable = false,
  parentDestinationType = null,
  pageId,
  adAccountId,
  onLeadFormNameChange,
  errors,
  existingImageUrl,
}) => {
  // Memoized so effects that depend on it don't loop (Copilot round 8
  // on PR #216) and so consumers pass a stable reference.
  const update = React.useCallback(
    (patch: Partial<MetaLinkCreativeSpec>) => {
      onChange((prev) => ({ ...prev, ...patch }));
    },
    [onChange],
  );

  // Lead-form picker — only for LEADS-objective parents. Same 5-min
  // staleTime as the ad-set wizard's per-Page fan-out.
  const leadFormsEnabled = Boolean(
    isLeadsFormPickerApplicable && pageId && adAccountId,
  );
  const {
    data: leadForms = [],
    isError: leadFormsErrored,
    isFetching: leadFormsFetching,
    refetch: refetchLeadForms,
  } = useQuery<MetaLeadForm[]>({
    queryKey: ['meta-lead-forms', pageId ?? '', adAccountId ?? ''],
    queryFn: () => metaAdsApi.listLeadForms(pageId as string, adAccountId as string),
    enabled: leadFormsEnabled,
    staleTime: 5 * 60 * 1000,
    // AdSetPromotedObjectStep's PageCard uses the same
    // ['meta-lead-forms', pageId, adAccountId] key with a 5-min
    // staleTime. Without `refetchOnMount: 'always'`, an empty
    // result cached during ad-set creation (transient Meta race
    // in the multi-page fan-out) would serve stale [] to this
    // picker and misrender as "No lead forms on this Page".
    refetchOnMount: 'always',
  });

  // Clear a stale `lead_gen_form_id` when the fetched list no longer
  // contains it (Page changed, forms deleted / refetched with a
  // different response). Without this the value stays set, validation
  // still passes because the string is non-empty, and the wizard
  // submits an invalid form_id that Meta rejects with a confusing
  // error. Guarded on !fetching/!error so a transient fetch-in-flight
  // (including the refetchOnMount-triggered refresh) doesn't blow
  // away a valid selection.
  React.useEffect(() => {
    if (leadFormsFetching || leadFormsErrored) return;
    if (!value.lead_gen_form_id) return;
    const stillExists = leadForms.some(
      (f) => f.id === value.lead_gen_form_id,
    );
    if (!stillExists) {
      update({ lead_gen_form_id: undefined });
    }
  }, [
    leadForms,
    leadFormsFetching,
    leadFormsErrored,
    value.lead_gen_form_id,
    update,
  ]);

  // Emit the picked form's name up so the Review step can render it
  // without re-fetching. `null` when nothing picked / list empty.
  React.useEffect(() => {
    if (!onLeadFormNameChange) return;
    if (!value.lead_gen_form_id) {
      onLeadFormNameChange(null);
      return;
    }
    const picked = leadForms.find((f) => f.id === value.lead_gen_form_id);
    onLeadFormNameChange(picked?.name ?? null);
  }, [leadForms, value.lead_gen_form_id, onLeadFormNameChange]);

  // Local UI state — which placement the picker is opened against.
  // `null` means closed; non-null means "open and scoped to this key".
  const [pickerPlacementKey, setPickerPlacementKey] = useState<
    string | null
  >(null);

  const handlePickerConfirm = (picked: PendingTargeted[]) => {
    // Single-image ad — take the first pick. (`TargetedImagePicker`
    // already enforces maxSelection=1.)
    onChangePendingImage(picked[0] ?? null);
    setPickerPlacementKey(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Build your image ad
        </h3>
        <p className="text-sm text-gray-600">
          Image + headline + copy + call-to-action. Meta will preview
          the ad before it serves.
        </p>
      </div>

      <AdvantagePlusCreativePanel
        value={advantagePlusCreative}
        onChange={onChangeAdvantagePlusCreative}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Image{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        {/* Wizard-only helper copy — in the editor (existingImageUrl
             set) the ad already has an image and the user just needs
             the swap affordance. */}
        {!existingImageUrl && !pendingImage && (
          <p className="text-xs text-gray-500 mb-3">
            Pick an aspect ratio, then choose an image from your Media
            Library. Meta will use this image for delivery; Advantage+
            Placements may crop it for other surfaces.
          </p>
        )}

        {pendingImage ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-gray-200 p-4">
            <div className="w-full max-w-[19rem] aspect-square rounded overflow-hidden bg-gray-100">
              <CroppedThumbnail
                source={pendingImage.source}
                cropBox={pendingImage.cropBox}
                className="w-full h-full"
              />
            </div>
            <div className="text-center space-y-0.5">
              <p className="text-sm font-medium text-gray-900">
                {pendingImage.source.original_filename ??
                  pendingImage.source.filename}
              </p>
              <p className="text-xs text-gray-500">
                Placement:{' '}
                {META_PLACEMENT_LABELS[pendingImage.placementKey] ??
                  pendingImage.placementKey}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() =>
                  setPickerPlacementKey(pendingImage.placementKey)
                }
              >
                Replace image
              </Button>
              <Button
                variant="secondary"
                onClick={() => onChangePendingImage(null)}
              >
                {existingImageUrl ? 'Revert' : 'Remove'}
              </Button>
            </div>
          </div>
        ) : existingImageUrl ? (
          // Editor context: an ad already has an image. Show it large +
          // centered with the Replace button below. "Remove" isn't
          // offered — Meta ads require an image, and removing without a
          // replacement would fail on save.
          <div className="flex flex-col items-center gap-3 rounded-md border border-gray-200 p-4">
            <div className="w-full max-w-[19rem] aspect-square rounded overflow-hidden bg-gray-100">
              <img
                src={existingImageUrl}
                alt="Current ad image"
                className="w-full h-full object-contain"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => setPickerPlacementKey('meta_ads.feed_square')}
            >
              Replace image
            </Button>
          </div>
        ) : (
          <div
            role="group"
            aria-label="Pick an aspect ratio"
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            {META_PLACEMENT_OPTIONS.map((opt) => (
              <button
                key={opt.placementKey}
                onClick={() => setPickerPlacementKey(opt.placementKey)}
                className="text-left border-2 border-dashed border-blue-300 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="text-sm font-semibold text-gray-900">
                  {opt.label}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Aspect ratio {opt.ratio}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {opt.description}
                </div>
              </button>
            ))}
          </div>
        )}

        {errors?.image && (
          <p className="text-sm text-red-600 mt-2">{errors.image}</p>
        )}

        <TargetedImagePicker
          isOpen={pickerPlacementKey !== null}
          onClose={() => setPickerPlacementKey(null)}
          usageContext={{
            platform: 'meta_ads',
            placementKey: pickerPlacementKey ?? 'meta_ads.feed_square',
          }}
          onConfirm={handlePickerConfirm}
          maxSelection={1}
        />
      </div>

      <div>
        {/* Inline counter layout — matches Google's RSAEditor pattern
             (`RSAEditor.tsx` line ~365): label full-width above, input
             + fixed-width counter on the same row so the input
             shortens to make room. `w-12 flex-shrink-0` gives the
             counter a consistent gutter. */}
        <label
          htmlFor="meta-creative-headline"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Headline{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        <div className="flex items-center gap-2">
          <Input
            id="meta-creative-headline"
            type="text"
            value={value.headline}
            onChange={(e) => update({ headline: e.target.value })}
            placeholder="Bold short text under the image"
            maxLength={LINK_CHAR_LIMITS.headline + 20}
            aria-invalid={Boolean(errors?.headline)}
            aria-describedby={
              errors?.headline ? 'meta-creative-headline-error' : undefined
            }
            required
            className="flex-1"
          />
          <span className="w-12 flex-shrink-0">
            <CharCount
              used={value.headline.length}
              cap={LINK_CHAR_LIMITS.headline}
            />
          </span>
        </div>
        {errors?.headline && (
          <p
            id="meta-creative-headline-error"
            className="text-sm text-red-600 mt-1"
          >
            {errors.headline}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-creative-primary-text"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Primary text{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        <div className="flex items-start gap-2">
          <textarea
            id="meta-creative-primary-text"
            value={value.primary_text}
            onChange={(e) => update({ primary_text: e.target.value })}
            placeholder="Long-form copy above the image"
            rows={3}
            maxLength={LINK_CHAR_LIMITS.primary_text + 50}
            aria-invalid={Boolean(errors?.primary_text)}
            aria-describedby={
              errors?.primary_text
                ? 'meta-creative-primary-text-error'
                : undefined
            }
            required
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {/* `pt-2` aligns the counter with the top of the textarea's
               first line — matches Google's RSAEditor row-3 pattern
               for multi-line descriptions. */}
          <span className="w-12 flex-shrink-0 pt-2">
            <CharCount
              used={value.primary_text.length}
              cap={LINK_CHAR_LIMITS.primary_text}
            />
          </span>
        </div>
        {errors?.primary_text && (
          <p
            id="meta-creative-primary-text-error"
            className="text-sm text-red-600 mt-1"
          >
            {errors.primary_text}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-creative-description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description{' '}
          <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <Input
            id="meta-creative-description"
            type="text"
            value={value.description ?? ''}
            onChange={(e) =>
              update({
                description: e.target.value === '' ? undefined : e.target.value,
              })
            }
            placeholder="Small text under the headline"
            maxLength={LINK_CHAR_LIMITS.description + 10}
            aria-invalid={Boolean(errors?.description)}
            aria-describedby={
              errors?.description
                ? 'meta-creative-description-error'
                : undefined
            }
            className="flex-1"
          />
          <span className="w-12 flex-shrink-0">
            <CharCount
              used={(value.description ?? '').length}
              cap={LINK_CHAR_LIMITS.description}
            />
          </span>
        </div>
        {errors?.description && (
          <p
            id="meta-creative-description-error"
            className="text-sm text-red-600 mt-1"
          >
            {errors.description}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-creative-cta"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Call-to-action
        </label>
        <select
          id="meta-creative-cta"
          value={value.call_to_action ?? 'LEARN_MORE'}
          onChange={(e) =>
            update({
              call_to_action: e.target.value as MetaCallToActionType,
            })
          }
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {CALL_TO_ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Non-ON_AD LEADS ad set — picker hidden; explain why so users
          understand this isn't a bug. The `isLeadsFormPickerApplicable`
          prop is already gated on ON_AD compatibility upstream, so seeing a
          concrete non-ON_AD destination_type here means the parent ad
          set can't host form-attached creatives (Messenger, Instagram
          DM, Phone call, Website). When the wizard sets a blocking
          error via `errors.lead_gen_form_id` (the LEADS-parent-but-
          non-ON_AD case), surface it here — the picker where it would
          normally render is hidden, so without this the user sees
          "Next" doing nothing (Copilot round 4 on PR #216). */}
      {!isLeadsFormPickerApplicable &&
        parentDestinationType &&
        parentDestinationType !== 'ON_AD' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="font-medium">
              This ad set doesn't use an instant lead form.
            </p>
            <p className="text-xs mt-1">
              The parent ad set's destination is{' '}
              <code className="text-xs">{parentDestinationType}</code>, so
              this ad clicks through to the destination URL instead of
              opening a lead form. Flouri doesn't yet support the
              Messenger / Instagram DM / Phone Call lead-ads variants
              through the wizard — create those ad sets in Meta Ads
              Manager directly for now.
            </p>
            {errors?.lead_gen_form_id && (
              <p
                role="alert"
                className="text-xs mt-2 font-medium text-red-700"
              >
                {errors.lead_gen_form_id}
              </p>
            )}
          </div>
        )}

      {isLeadsFormPickerApplicable && (
        <div>
          <label
            htmlFor="meta-creative-lead-form"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Lead form{' '}
            <span className="text-red-500" aria-label="required">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            The instant form users see after tapping the CTA. Meta attaches
            it to the ad creative for LEADS-objective campaigns; without a
            form the ad will be rejected on submit.
          </p>
          <select
            id="meta-creative-lead-form"
            value={value.lead_gen_form_id ?? ''}
            onChange={(e) =>
              update({
                lead_gen_form_id:
                  e.target.value === '' ? undefined : e.target.value,
              })
            }
            disabled={leadFormsErrored || leadForms.length === 0}
            aria-invalid={Boolean(errors?.lead_gen_form_id)}
            aria-describedby={
              errors?.lead_gen_form_id
                ? 'meta-creative-lead-form-error'
                : undefined
            }
            required
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">
              {leadFormsFetching && leadForms.length === 0
                ? 'Loading lead forms…'
                : leadFormsErrored
                ? "Couldn't load lead forms"
                : leadForms.length === 0
                ? 'No lead forms on this Page'
                : 'Select a lead form'}
            </option>
            {leadForms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name || f.id}
              </option>
            ))}
          </select>
          {leadFormsErrored && (
            // Retry lives outside the disabled <select> so the user has
            // an actionable path (Copilot round 5 on PR #216 — the
            // previous "try again" text was inside the disabled option
            // and unreachable). React Query's refetch is idempotent;
            // isFetching disables the button while a retry is in flight.
            <div className="text-xs text-red-700 mt-1 flex items-center gap-2">
              <span>Fetch failed.</span>
              <button
                type="button"
                onClick={() => refetchLeadForms()}
                disabled={leadFormsFetching}
                className="underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {leadFormsFetching ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          )}
          {leadForms.length === 0 && !leadFormsFetching && !leadFormsErrored && (
            <p className="text-xs text-amber-700 mt-1">
              Create a lead form on this Page in Meta Business Suite, then
              return to this wizard.
            </p>
          )}
          {errors?.lead_gen_form_id && (
            <p
              id="meta-creative-lead-form-error"
              className="text-sm text-red-600 mt-1"
            >
              {errors.lead_gen_form_id}
            </p>
          )}
        </div>
      )}

      <div>
        <label
          htmlFor="meta-creative-link-url"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Destination URL{' '}
          <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-creative-link-url"
          type="url"
          value={value.link_url}
          onChange={(e) => update({ link_url: e.target.value })}
          placeholder="https://yoursite.com/landing-page"
          aria-invalid={Boolean(errors?.link_url)}
          aria-describedby={
            errors?.link_url ? 'meta-creative-link-url-error' : undefined
          }
          required
        />
        {errors?.link_url && (
          <p
            id="meta-creative-link-url-error"
            className="text-sm text-red-600 mt-1"
          >
            {errors.link_url}
          </p>
        )}
      </div>
    </div>
  );
};
